import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, FormLabel, FormGroup, TextField, Tooltip, IconButton, Icon, Button } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DataGrid } from '@material-ui/data-grid';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';

import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    searchButton: {
        width: '100%',
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    tableContainer: {
        width: '100%',
        marginBottom: 20
    },
}));

const defProduct = { id: 0, name: 'All products' };
const defType = { id: 0, name: 'All types' };
const defCategory = { id: 0, name: 'All categories' };

const KardexIndex = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        data: [],
        products: [],
        types: [],
        categories: [],
        filter: {
            product: defProduct,
            type: defType,
            category: defCategory
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Kardex - Index';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/invKardex');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                data: response?.data ?? [],
                products: response.products,
                types: response.types,
                categories: response.categories
            }));
        } else {
            console.log('[KardexIndex] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const search = async () => {
        showLoader(true);
        const response = await get('/invKardex/search', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response.data }));

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[KardexIndex] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const valueGetter = ({ value }) => value?.name ?? '?';
    const getStock = ({ field, row }) => (row?.kardex[0]) ? row?.kardex[0][field] : 'N/A';
    const getLastUpdate = ({ row }) => row?.kardex[0]?.date ? dayjs(row.kardex[0].date).utc().local().format('MM/DD/YYYY HH:mm') : 'N/A';
    const getTotal = ({ row }) => (row?.kardex[0]) ? Number(row.kardex[0].balance) + Number(row.kardex[0].storage_balance) : 'N/A';
    const renderCell = ({ row }) => {
        return (
            <Link to={`/inventory/kardex/${row.idproduct_type_ref}`}>
                <Tooltip title='Show kardex activity' placement='top'>
                    <IconButton>
                        <Icon>{'visibility'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'product', headerName: 'Product', flex: 1, minWidth: 180, valueGetter },
        { field: 'type', headerName: 'Type', flex: 1, minWidth: 180, valueGetter },
        { field: 'qty', headerName: 'Pack qty', flex: 1, minWidth: 100 },
        { field: 'balance', headerName: 'Stock', flex: 1, minWidth: 100, type: 'number', valueGetter: getStock },
        { field: 'storage_balance', headerName: 'Storage stock', flex: 1, minWidth: 100, type: 'number', valueGetter: getStock },
        { field: 'total', headerName: 'Total', flex: 1, minWidth: 100, type: 'number', valueGetter: getTotal },
        { field: 'last_update', headerName: 'Last update', flex: 1, minWidth: 100, valueGetter: getLastUpdate },
        { field: 'actions', headerName: 'Actions', flex: 0.5, minWidth: 100, renderCell }
    ]

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={12} md={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Product'}</FormLabel>

                        <Autocomplete
                            options={state.products}
                            value={state.filter.product}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, product) => handleAutocomplete('product', product)}
                            renderInput={params => <TextField {...params} placeholder='Product' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Type'}</FormLabel>

                        <Autocomplete
                            options={state.types}
                            value={state.filter.type}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, type) => handleAutocomplete('type', type)}
                            renderInput={params => <TextField {...params} placeholder='Type' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Category'}</FormLabel>

                        <Autocomplete
                            options={state.categories}
                            value={state.filter.category}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, type) => handleAutocomplete('category', type)}
                            renderInput={params => <TextField {...params} placeholder='Category' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2} className={classes.buttonContainer}>
                    <Button
                        variant='contained'
                        className={classes.searchButton}
                        onClick={search}
                    >
                        {'Search'}
                    </Button>
                </Grid>
            </Grid>

            <div className={classes.tableContainer}>
                <DataGrid
                    autoHeight
                    disableSelectionOnClick
                    pageSize={45}
                    density='compact'
                    rows={state.data}
                    columns={columns}
                    getRowId={row => row.idproduct_type_ref}
                    getRowClassName={({ row }) => (row.error) ? 'row-error' : ''}
                />
            </div>
        </div>
    );
}

export default KardexIndex;